import { SpaceDevice } from 'core/rtbCampaign/RtbCampaign';
import {
  SpaceHealthState,
  SPACE_HEALTH_MATCH_PRIORITY
} from 'enum/SpaceHealthState';

/**
 * Aggregated operational status for a chip that may cover one or more spaces
 * (e.g. a store chip maps to every device at that store).
 */
export type AggregatedSpaceHealth = {
  state: SpaceHealthState;
  /** Reason text (only meaningful for `banned`). */
  reason?: string;
  /** Representative space name for the tooltip header. */
  spaceName?: string;
  /** ISO timestamp of the latest status sync, for the tooltip footer. */
  lastSyncedAt?: string;
  /**
   * 這組版位全部都已在 RMax 刪除。只要還有一台沒刪，這個投遞目標就還投得出去，
   * 所以只看沒刪除的版位的運作狀態，不算已刪除。單台層級的 chip 傳進來的就只有那一台。
   */
  deleted: boolean;
};

/**
 * 依聚合匹配優先順序，從多個版位挑出勝出狀態：只要有一筆還在正常服務就回傳
 * ON（正常），全部都停了才回傳 BANNED（見 SPACE_HEALTH_MATCH_PRIORITY）。
 * 沒有回報狀態的版位一律視為 `normal`，所以配對到的門市一定會有結果（正常或
 * 異常）—— 呼叫端會為每個配對到的 chip 渲染狀態 tooltip。只有在完全沒有配對
 * 到版位時（例如不是單一版位的群組 chip）才回傳 undefined。
 * 同優先序時保留最先出現的版位（用 `<` 而非 `<=`），reason / spaceName /
 * lastSyncedAt 皆從該勝出版位帶出。
 */
export function aggregateHealthState(
  spaces: SpaceDevice[]
): AggregatedSpaceHealth | undefined {
  if (spaces.length === 0) return undefined;

  const stateOf = (space: SpaceDevice): SpaceHealthState =>
    space.healthState ?? SpaceHealthState.NORMAL;

  // 還有沒刪除的版位時只從它們之中挑：已刪除版位留下的健康狀態不該蓋掉還在運作的那台。
  const liveSpaces = spaces.filter((space) => space.active !== false);
  const deleted = liveSpaces.length === 0;
  const candidates = deleted ? spaces : liveSpaces;

  let best = candidates[0];
  let bestPriority = SPACE_HEALTH_MATCH_PRIORITY[stateOf(best)];
  for (const space of candidates) {
    const priority = SPACE_HEALTH_MATCH_PRIORITY[stateOf(space)];
    if (priority < bestPriority) {
      bestPriority = priority;
      best = space;
    }
  }

  return {
    state: stateOf(best),
    reason: best.spaceStatusModifyReason,
    spaceName: best.spaceName,
    lastSyncedAt: best.lastSyncedAt,
    deleted
  };
}
